import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Heading,
    Button,
    SimpleGrid,
    Card,
    CardBody,
    Image,
    Text,
    Stack,
    HStack,
    Spinner,
    Center,
    Container,
    Select,
    useToast,
    VStack,
    Badge,
    Flex
} from '@chakra-ui/react';
import { PhoneIcon } from '@chakra-ui/icons';
import { GpsFixed } from "@mui/icons-material"; // Import icon
import { businessCategories } from "../../resources/Catagories"; // Import business categories
import { API_BASE_URL } from "../../config/api_keys"; // Import API base URL

const BusinessmenList = () => {
    const [businessmen, setBusinessmen] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const toast = useToast();

    // State for dropdown options
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [areas, setAreas] = useState([]);
    
    // State for selected filters
    const [filters, setFilters] = useState({ 
        state: '', 
        city: '', 
        area: '', 
        category: '' 
    });

    // State for location filters
    const [radius, setRadius] = useState('');
    const [userLocation, setUserLocation] = useState(null);
    const [isLocating, setIsLocating] = useState(false);

    // Fetch states when component mounts
    useEffect(() => {
        const fetchStates = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/businessmen/locations`);
                if (response.data.success) {
                    setStates(response.data.states);
                }
            } catch (error) {
                console.error("Error fetching states:", error);
            }
        };
        
        fetchStates();
    }, []);

    // Fetch cities when state changes
    useEffect(() => {
        const fetchCities = async () => {
            if (filters.state) {
                try {
                    const response = await axios.get(`${API_BASE_URL}/api/businessmen/cities/${filters.state}`);
                    if (response.data.success) {
                        setCities(response.data.cities);
                        // Reset city and area when state changes
                        setFilters(prev => ({ ...prev, city: '', area: '' }));
                        setAreas([]);
                    }
                } catch (error) {
                    console.error("Error fetching cities:", error);
                }
            } else {
                setCities([]);
                setAreas([]);
                setFilters(prev => ({ ...prev, city: '', area: '' }));
            }
        };
        
        fetchCities();
    }, [filters.state]);

    // Fetch areas when city changes
    useEffect(() => {
        const fetchAreas = async () => {
            if (filters.state && filters.city) {
                try {
                    const response = await axios.get(`${API_BASE_URL}/api/businessmen/areas/${filters.state}/${filters.city}`);
                    if (response.data.success) {
                        setAreas(response.data.areas);
                        // Reset area when city changes
                        setFilters(prev => ({ ...prev, area: '' }));
                    }
                } catch (error) {
                    console.error("Error fetching areas:", error);
                }
            } else {
                setAreas([]);
                setFilters(prev => ({ ...prev, area: '' }));
            }
        };
        
        fetchAreas();
    }, [filters.state, filters.city]);

    useEffect(() => {
        const fetchBusinessmen = async () => {
            setIsLoading(true);
            try {
                // Build query parameters dynamically
                const params = {};
                if (userLocation && radius) {
                    params.latitude = userLocation.latitude;
                    params.longitude = userLocation.longitude;
                    params.radius = radius;
                } else {
                    params.state = filters.state;
                    params.city = filters.city;
                    params.area = filters.area;
                    params.category = filters.category; // Include category in API request
                }

                const { data } = await axios.get(`${API_BASE_URL}/api/businessmen-list`, { params });
                setBusinessmen(data.data || data); // Handle both response formats
            } catch (error) {
                console.error("Error fetching businessmen:", error);
                toast({ title: "Error", description: "Could not fetch businessmen.", status: "error", isClosable: true });
            } finally {
                setIsLoading(false);
            }
        };

        // Debounce fetching to avoid too many API calls
        const handler = setTimeout(() => {
            fetchBusinessmen();
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [filters, userLocation, radius, toast]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        // When using text filters, clear location filters to avoid confusion
        setUserLocation(null);
        setRadius('');
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleFindNearMe = () => {
        if (!navigator.geolocation) {
            return toast({ title: "Geolocation not supported", description: "Your browser does not support geolocation.", status: "error", isClosable: true });
        }

        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                // When using location filter, clear text filters
                setFilters({ state: '', city: '', area: '', category: '' });
                setUserLocation({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude
                });
                // Set a default radius if none is selected
                if (!radius) setRadius('10'); // Default to 10km
                setIsLocating(false);
                toast({ title: "Location found!", status: "success", isClosable: true });
            },
            () => {
                setIsLocating(false);
                toast({ title: "Unable to retrieve your location", description: "Please enable location permissions in your browser.", status: "error", isClosable: true });
            }
        );
    };

    const clearFilters = () => {
        setFilters({ state: '', city: '', area: '', category: '' });
        setUserLocation(null);
        setRadius('');
    };

    // Helper function to get location data from either nested location or top-level fields
    const getLocationData = (user) => {
        if (user.location) {
            return {
                state: user.location.state || '',
                city: user.location.city || '',
                area: user.location.area || ''
            };
        }
        return {
            state: user.state || '',
            city: user.city || '',
            area: user.area || ''
        };
    };

    return (
        <Container maxW="container.xl" py={8}>
            <Heading as="h1" size="xl" mb={6} textAlign="center">
                Find Trader
            </Heading>

            {/* Filter Section */}
            <VStack spacing={4} mb={8} p={{ base: 4, md: 6 }} borderWidth="1px" borderRadius="lg" align="stretch" bg="gray.50" boxShadow="sm">
                <Heading size="md" textAlign="center" color="gray.700" mb={2}>Filter Traders</Heading>
                
                {/* Main Filter Controls - Responsive Grid Layout */}
                <Box>
                    <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={{ base: 3, md: 4 }} mb={4}>
                        {/* Category Filter */}
                        <Box>
                            <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">Category</Text>
                            <Select 
                                name="category" 
                                placeholder="All Categories" 
                                value={filters.category} 
                                onChange={handleFilterChange}
                                size="md"
                                borderRadius="md"
                            >
                                {businessCategories.map((category, index) => (
                                    <option key={index} value={category}>{category}</option>
                                ))}
                            </Select>
                        </Box>
                        
                        {/* State Dropdown */}
                        <Box>
                            <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">State</Text>
                            <Select 
                                name="state" 
                                placeholder="Select State" 
                                value={filters.state} 
                                onChange={handleFilterChange} 
                                size="md"
                                borderRadius="md"
                            >
                                {states.map((state, index) => (
                                    <option key={index} value={state}>{state}</option>
                                ))}
                            </Select>
                        </Box>
                        
                        {/* City Dropdown */}
                        <Box>
                            <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">City</Text>
                            <Select 
                                name="city" 
                                placeholder="Select City" 
                                value={filters.city} 
                                onChange={handleFilterChange} 
                                size="md"
                                borderRadius="md"
                                isDisabled={!filters.state}
                            >
                                {cities.map((city, index) => (
                                    <option key={index} value={city}>{city}</option>
                                ))}
                            </Select>
                        </Box>
                        
                        {/* Area Dropdown */}
                        
                        {/* <Box>
                            <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">Area</Text>
                            <Select 
                                name="area" 
                                placeholder="Select Area" 
                                value={filters.area} 
                                onChange={handleFilterChange} 
                                size="md"
                                borderRadius="md"
                                isDisabled={!filters.city}
                            >
                                {areas.map((area, index) => (
                                    <option key={index} value={area}>{area}</option>
                                ))}
                            </Select>
                        </Box> */}
                    </SimpleGrid>
                    
                    {/* Location Controls - Separate Row for Better Layout */}
                    <Box bg="white" p={4} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                        <Text fontSize="sm" fontWeight="medium" mb={3} color="gray.600">Location-Based Search</Text>
                        <Flex direction={{ base: 'column', md: 'row' }} gap={4} align="end">
                            <Button
                                colorScheme="teal"
                                leftIcon={<GpsFixed />}
                                onClick={handleFindNearMe}
                                isLoading={isLocating}
                                size="md"
                                variant="outline"
                                flex={{ md: '0 0 auto' }}
                                width={{ base: '100%', md: 'auto' }}
                            >
                                Find Near Me
                            </Button>
                            
                            <Box flex="1" maxWidth={{ md: '200px' }}>
                                <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">Radius (km)</Text>
                                <Select
                                    placeholder="Select Radius"
                                    value={radius}
                                    onChange={(e) => {
                                        setRadius(e.target.value);
                                        if (!userLocation && e.target.value) {
                                            handleFindNearMe();
                                        }
                                    }}
                                    size="md"
                                    borderRadius="md"
                                >
                                    <option value="5">5 km</option>
                                    <option value="10">10 km</option>
                                    <option value="25">25 km</option>
                                    <option value="50">50 km</option>
                                    <option value="100">100 km</option>
                                </Select>
                            </Box>
                            
                            <Button 
                                onClick={clearFilters} 
                                colorScheme="red" 
                                variant="outline" 
                                size="md"
                                width={{ base: '100%', md: 'auto' }}
                            >
                                Clear All Filters
                            </Button>
                        </Flex>
                    </Box>
                </Box>
                
                {/* Active Filters Display */}
                {(filters.category || filters.state || filters.city || filters.area || userLocation) && (
                    <Box mt={4} pt={4} borderTop="1px solid" borderTopColor="gray.200">
                        <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">Active Filters:</Text>
                        <HStack spacing={2} flexWrap="wrap">
                            {filters.category && <Badge colorScheme="blue" px={3} py={1}>{filters.category}</Badge>}
                            {filters.state && <Badge colorScheme="green" px={3} py={1}>State: {filters.state}</Badge>}
                            {filters.city && <Badge colorScheme="green" px={3} py={1}>City: {filters.city}</Badge>}
                            {filters.area && <Badge colorScheme="green" px={3} py={1}>Area: {filters.area}</Badge>}
                            {userLocation && <Badge colorScheme="purple" px={3} py={1}>Near Me ({radius || 10} km)</Badge>}
                        </HStack>
                    </Box>
                )}
            </VStack>

            {/* Display Section */}
            {isLoading ? (
                <Center h="40vh">
                    <Spinner size="xl" />
                </Center>
            ) : businessmen.length === 0 ? (
                <Box textAlign="center" py={12}>
                    <Text fontSize="lg" color="gray.600">No traders found with the current filters.</Text>
                    <Button mt={4} onClick={clearFilters} colorScheme="teal" variant="outline">
                        Clear Filters
                    </Button>
                </Box>
            ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 2, xl: 3 }} spacing={8}>
                    {businessmen.map((user) => {
                        const locationData = getLocationData(user);
                        return (
                            <Card key={user._id} borderWidth="1px" borderRadius="lg" overflow="hidden" boxShadow="md" transition="all 0.2s" _hover={{ boxShadow: "lg", transform: "translateY(-2px)" }}>
                                <CardBody>
                                    <Center>
                                        <Image
                                            src={user.picture}
                                            alt={user.name}
                                            borderRadius="full"
                                            boxSize={{ base: "100px", md: "120px" }}
                                            objectFit="cover"
                                            mb={4}
                                            border="2px solid"
                                            borderColor="gray.100"
                                        />
                                    </Center>
                                    <Stack mt="6" spacing="3" textAlign="center">
                                        <Heading size="md" noOfLines={1}>{user.name}</Heading>
                                        {user.businessCategory && (
                                            <Badge colorScheme="teal" fontSize="sm" px={3} py={1} borderRadius="full">
                                                {user.businessCategory}
                                            </Badge>
                                        )}
                                        <Text color="blue.600" fontSize="sm" noOfLines={2}>
                                            {locationData.area}, {locationData.city}, {locationData.state}
                                        </Text>
                                        <HStack justify="center" spacing={2}>
                                            <PhoneIcon color="gray.500" boxSize={4} />
                                            <Text fontSize="sm" fontWeight="medium">
                                                {user.phonenumber}
                                            </Text>
                                        </HStack>
                                    </Stack>
                                </CardBody>
                            </Card>
                        );
                    })}
                </SimpleGrid>
            )}
        </Container>
    );
};

export default BusinessmenList;